import { EventRejectionReason } from "../domain/rejectionReason.interface";
import { instanceBo } from "../utils/axios.create";

/**
 * How far a reason has spread — SIMS-8498 AC6's count and AC7's count.
 *
 * 🔴 `eventsWithOwnWording` is computed server-side by INEQUALITY, not by field
 * presence. Restoring an override leaves `originalParticipantCopy` in place, so
 * counting by presence would report every event that ever overrode, including
 * the ones that changed back.
 */
export interface ReasonReach {
	eventsHolding: number;
	eventsWithOwnWording: number;
	blocked: { eventId: string; eventName?: string; why: string }[];
}

export interface CreateAccountReasonInput {
	title: string;
	participantCopy: string;
	description?: string;
	suggestedFix?: string;
	requiresNote?: boolean;
}

export type UpdateAccountReasonInput = Partial<CreateAccountReasonInput>;

const BASE = "/events/rejection-reasons";

/** AC1 — everything this account can draw on, each carrying its tier. */
const list = async (): Promise<EventRejectionReason[]> =>
	instanceBo
		.get(BASE)
		.then((res) => res.data?.body ?? [])
		.catch((err) => Promise.reject(err.response?.data ?? err));

/**
 * AC2 — create a reason for this account.
 *
 * 🔴 No code is sent. It is derived from the title by the server and issued
 * once; AC7 then makes it immutable, because rejection counts aggregate on it
 * and a reissued code would split one historical series in two.
 */
const create = async (
	input: CreateAccountReasonInput,
): Promise<EventRejectionReason> =>
	instanceBo
		.post(BASE, input)
		.then((res) => res.data?.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

/**
 * AC7 — correct the wording. Returns the reach alongside the reason, because
 * the administrator has to be told how many events hold their own wording and
 * this is the call that changes it.
 */
const update = async (
	code: string,
	input: UpdateAccountReasonInput,
): Promise<{ reason: EventRejectionReason; reach: ReasonReach }> =>
	instanceBo
		.put(`${BASE}/${encodeURIComponent(code)}`, input)
		.then((res) => res.data?.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

/** How far it has spread, for the confirmation before retiring. */
const reach = async (code: string): Promise<ReasonReach> =>
	instanceBo
		.get(`${BASE}/${encodeURIComponent(code)}/reach`)
		.then((res) => res.data?.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

/**
 * AC6 — retire, with the reach the administrator chose.
 *
 * 🔴 `withdrawFromEvents` defaults to FALSE and must be passed explicitly. The
 * stronger reach removes the reason from every event that holds it, taking each
 * event's own wording with it — that sentence cannot be recovered by re-adding
 * the reason. Never send it because a checkbox was merely truthy.
 */
const retire = async (
	code: string,
	withdrawFromEvents = false,
): Promise<ReasonReach> =>
	instanceBo
		.post(`${BASE}/${encodeURIComponent(code)}/retire`, {
			withdrawFromEvents: withdrawFromEvents === true,
		})
		.then((res) => res.data?.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

/**
 * AC4 — promote an event-only reason to the account.
 *
 * Addressed by EVENT, because promotion happens where the reason already lives.
 * The code does not change; the source event's badge does.
 */
const promoteFromEvent = async (
	eventId: string,
	code: string,
): Promise<EventRejectionReason> =>
	instanceBo
		.post(
			`/events/${encodeURIComponent(eventId)}/rejection-reasons/${encodeURIComponent(
				code,
			)}/promote`,
			{},
		)
		.then((res) => res.data?.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

export const accountRejectionReasonService = {
	list,
	create,
	update,
	reach,
	retire,
	promoteFromEvent,
};
